import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { pluralise } from '@/lib/format'
import {
  QUESTION_TYPE_LABEL,
  QUESTION_TYPES,
  cleanQuestion,
  questionId,
  questionProblems,
  type Question,
  type QuestionType,
} from '@/lib/questionnaire'
import { describeError } from '@/lib/supabase'
import { QuestionnaireForm } from './QuestionnaireForm'
import { useQuestionnaireTemplate, useUpdateQuestionnaireTemplate, type QuestionnaireTemplate } from './queries'

/**
 * One questionnaire template, under Settings › Templates (29 Oct 2026): the
 * wording at the top, then each question in order, and a preview of what the
 * couple sees. Edits stay on this page until Save. Questionnaires already
 * sent keep their own copy, so a change here only affects new ones.
 */
export function QuestionnaireTemplatePage() {
  const { id = '' } = useParams()
  const template = useQuestionnaireTemplate(id)

  if (template.isPending) return <Loading label="Opening the questionnaire" />
  if (template.isError)
    return (
      <Page>
        <ErrorState title="The questionnaire could not open" detail={describeError(template.error)} onRetry={() => void template.refetch()} />
      </Page>
    )
  return <Editor key={template.data.updated_at} template={template.data} />
}

type Draft = { name: string; intro: string; questions: Question[] }

const draftOf = (template: QuestionnaireTemplate): Draft => ({
  name: template.name,
  intro: template.intro ?? '',
  questions: template.questions,
})

function Editor({ template }: { template: QuestionnaireTemplate }) {
  const update = useUpdateQuestionnaireTemplate(template.id)
  const [draft, setDraft] = useState<Draft>(() => draftOf(template))
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  const [newType, setNewType] = useState<QuestionType>('short')

  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(template))
  const problems = [...(draft.name.trim() ? [] : ['The questionnaire needs a name.']), ...questionProblems(draft.questions)]

  const setQuestion = (index: number, patch: Partial<Question>) =>
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question, i) => (i === index ? { ...question, ...patch } : question)),
    }))

  const move = (index: number, by: -1 | 1) =>
    setDraft((current) => {
      const questions = [...current.questions]
      const [item] = questions.splice(index, 1)
      if (item) questions.splice(index + by, 0, item)
      return { ...current, questions }
    })

  const remove = (index: number) => {
    const question = draft.questions[index]
    if (question?.label.trim() && !window.confirm(`Remove "${question.label.trim()}"?`)) return
    setDraft((current) => ({ ...current, questions: current.questions.filter((_, i) => i !== index) }))
  }

  const add = () =>
    setDraft((current) => {
      const id = questionId(`new ${newType}`, current.questions.map((question) => question.id))
      const question: Question = { id, type: newType, label: '', ...(newType === 'choice' ? { options: ['', ''] } : {}) }
      return { ...current, questions: [...current.questions, question] }
    })

  function save() {
    update.mutate(
      { name: draft.name.trim(), intro: draft.intro.trim() || null, questions: draft.questions.map(cleanQuestion) },
      { onSuccess: () => toast.success('Questionnaire saved') },
    )
  }

  return (
    <div className="max-w-[60rem] pt-5">
      <PageHeader
        eyebrow="Questionnaire template"
        title={draft.name || 'Untitled questionnaire'}
        meta={
          <>
            <Link to="/settings/templates" className="underline underline-offset-4">
              All templates
            </Link>
            <span>{pluralise(draft.questions.filter((question) => question.type !== 'note').length, 'question')}</span>
            {template.is_default ? <Badge tone="sage">Default</Badge> : null}
          </>
        }
        actions={
          <>
            <div className="flex rounded-md bg-[var(--color-wash-strong)] p-0.5">
              {(['edit', 'preview'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={cn(
                    'rounded px-3 py-1 text-[length:var(--text-xs)] font-medium',
                    view === key ? 'bg-[var(--surface-card)] shadow-[var(--shadow-card)]' : 'text-taupe',
                  )}
                  onClick={() => setView(key)}
                >
                  {key === 'edit' ? 'Edit' : 'Preview'}
                </button>
              ))}
            </div>
            <Button variant="primary" disabled={!dirty || problems.length > 0} loading={update.isPending} onClick={save}>
              Save
            </Button>
          </>
        }
      />

      {dirty && problems.length ? (
        <p className="mb-4 text-[length:var(--text-sm)] text-amber">To save: {problems.join(' ')}</p>
      ) : dirty ? (
        <p className="mb-4 text-[length:var(--text-sm)] text-taupe">You have changes that aren't saved yet.</p>
      ) : null}

      {view === 'preview' ? (
        <Section>
          <QuestionnaireForm name={draft.name} intro={draft.intro} questions={draft.questions} />
        </Section>
      ) : (
        <>
          <Section title="Wording at the top">
            <div className="flex flex-col gap-4">
              <Field label="Name">
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </Field>
              <Field label="Introduction" hint="Shown above the first question.">
                <Textarea rows={5} value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
              </Field>
            </div>
          </Section>

          <ol className="flex flex-col gap-3">
            {draft.questions.map((question, index) => (
              <QuestionEditor
                key={question.id}
                number={index + 1}
                question={question}
                first={index === 0}
                last={index === draft.questions.length - 1}
                onChange={(patch) => setQuestion(index, patch)}
                onMove={(by) => move(index, by)}
                onRemove={() => remove(index)}
              />
            ))}
          </ol>

          <div className="card mt-3 flex flex-wrap items-end gap-3 p-4">
            <Field label="Add a question" className="w-[14rem]">
              <Select value={newType} onChange={(e) => setNewType(e.target.value as QuestionType)}>
                {QUESTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {QUESTION_TYPE_LABEL[type]}
                  </option>
                ))}
              </Select>
            </Field>
            <Button onClick={add}>Add</Button>
          </div>
        </>
      )}
    </div>
  )
}

function QuestionEditor({
  number,
  question,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  number: number
  question: Question
  first: boolean
  last: boolean
  onChange: (patch: Partial<Question>) => void
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}) {
  const note = question.type === 'note'
  return (
    <li className="card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="label">{number}</span>
          <Select
            aria-label="Kind of question"
            className="w-[13rem]"
            value={question.type}
            onChange={(e) => {
              const type = e.target.value as QuestionType
              onChange(type === 'choice' && !question.options?.length ? { type, options: ['', ''] } : { type })
            }}
          >
            {QUESTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {QUESTION_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" disabled={first} onClick={() => onMove(-1)} aria-label="Move up">
            ↑
          </Button>
          <Button size="sm" variant="ghost" disabled={last} onClick={() => onMove(1)} aria-label="Move down">
            ↓
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove}>
            Remove
          </Button>
        </div>
      </div>

      <Field label={note ? 'Heading' : 'Question'}>
        <Textarea
          rows={Math.max(1, Math.ceil(question.label.length / 80))}
          value={question.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </Field>
      <Field label={note ? 'Text' : 'Help text'} {...(note ? {} : { hint: 'Optional. Smaller text under the question.' })}>
        <Input value={question.help ?? ''} onChange={(e) => onChange({ help: e.target.value })} />
      </Field>

      {question.type === 'choice' ? (
        <Field label="Choices" hint="One per line. The couple picks one.">
          <Textarea
            rows={Math.max(2, (question.options ?? []).length)}
            value={(question.options ?? []).join('\n')}
            onChange={(e) => onChange({ options: e.target.value.split('\n') })}
          />
        </Field>
      ) : null}

      {!note ? (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            label="Must be answered"
            checked={Boolean(question.required)}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          <Checkbox
            label="Half width"
            description="Sits beside the next question if that one is half width too."
            checked={Boolean(question.half)}
            onChange={(e) => onChange({ half: e.target.checked })}
          />
        </div>
      ) : null}
    </li>
  )
}
